/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: TikTok Video Generator 新 Hero Section - 左文右视频布局
 */
import { useRouter } from 'next/router';
import React, { useContext, useRef, useState } from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { RightContext } from '@/components/RightProvider/context';

import styles from './index.module.scss';

// 静音图标
const MutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
  >
    <g clipPath="url(#clip0_muted)">
      <path opacity="0.01" d="M12.9 5.40002H9V9.30002H12.9V5.40002Z" fill="#333333" />
      <mask
        id="mask0_muted"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="9"
        y="5"
        width="4"
        height="5"
      >
        <path d="M12.9 5.40002H9V9.30002H12.9V5.40002Z" fill="#333333" />
      </mask>
      <g mask="url(#mask0_muted)">
        <path
          d="M12.2204 6.08575L9.6748 8.63134"
          stroke="#333333"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.6748 6.08575L12.2204 8.63134"
          stroke="#333333"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <path
        d="M7.2002 1.80002V12.6C5.1002 12.6 3.53975 9.85175 3.53975 9.85175H1.8002C1.46882 9.85175 1.2002 9.58313 1.2002 9.25175V5.10326C1.2002 4.77188 1.46882 4.50326 1.8002 4.50326H3.53975C3.53975 4.50326 5.1002 1.80002 7.2002 1.80002Z"
        stroke="#333333"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_muted">
        <rect width="14.4" height="14.4" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// 非静音图标
const UnmutedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
  >
    <path
      d="M7.19922 1.80002V12.6C5.09922 12.6 3.53877 9.85175 3.53877 9.85175H1.79922C1.46785 9.85175 1.19922 9.58313 1.19922 9.25175V5.10326C1.19922 4.77188 1.46785 4.50326 1.79922 4.50326H3.53877C3.53877 4.50326 5.09922 1.80002 7.19922 1.80002Z"
      stroke="#333333"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M9.59961 4.5C9.78657 4.66695 9.95604 4.85391 10.1048 5.05764C10.5412 5.65512 10.7996 6.39669 10.7996 7.2C10.7996 7.99635 10.5457 8.73201 10.1161 9.32679C9.96465 9.53655 9.79134 9.72879 9.59961 9.9"
      stroke="#333333"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface TikTokHeroNewProps {
  /** 标题 */
  title: string;
  /** 副标题 */
  subtitle: string;
  /** CTA 按钮文本 */
  ctaText: string;
  /** 跳转链接 */
  ctaUrl?: string;
  /** 视频列表 */
  videos?: string[];
  /** 封面图列表 */
  posters?: string[];
}

const TikTokHeroNew: React.FC<TikTokHeroNewProps> = ({
  title,
  subtitle,
  ctaText,
  ctaUrl = '/ai-video-generator',
  videos = [],
  posters = [],
}) => {
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  // 处理声音按钮点击
  const handleSoundClick = (index: number) => {
    // 如果点击的是当前播放的视频，则静音
    if (playingIndex === index) {
      const video = videoRefs.current[index];
      if (video) {
        video.muted = true;
      }
      setPlayingIndex(null);
      return;
    }

    // 静音其他所有视频
    Object.values(videoRefs.current).forEach((video) => {
      if (video) {
        video.muted = true;
      }
    });

    // 取消静音当前视频
    const video = videoRefs.current[index];
    if (video) {
      video.muted = false;
      video.play().catch(() => {
        // 自动播放可能被阻止
      });
      setPlayingIndex(index);
    }
  };

  // CTA 点击
  const handleCtaClick = async () => {
    if (!rightContext.getIsLoggedIn()) {
      await rightContext.getUserLoginState({
        onReady: () => {
          router.push(ctaUrl);
        },
      });
      return;
    }
    router.push(ctaUrl);
  };

  // 默认展示 3 个视频位
  const displayVideos = videos.length > 0 ? videos.slice(0, 3) : ['', '', ''];

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 左侧内容区域 */}
        <div className={styles.content}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
          <div className={styles.ctaWrapper}>
            <StartButton
              text={ctaText}
              onClick={handleCtaClick}
              className={styles.ctaButton}
            />
          </div>
        </div>

        {/* 右侧视频区域 */}
        <div className={styles.videosWrapper}>
          {displayVideos.map((videoUrl, index) => (
            <div key={index} className={styles.videoItem}>
              <div className={styles.videoContainer}>
                {videoUrl ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    src={videoUrl}
                    poster={posters[index] || ''}
                    className={styles.video}
                    loop
                    playsInline
                    muted
                    autoPlay
                    preload="metadata"
                  />
                ) : posters[index] ? (
                  <img src={posters[index]} alt="" className={styles.video} />
                ) : (
                  <div className={styles.placeholder} />
                )}

                {/* 声音按钮 */}
                <button
                  type="button"
                  className={styles.soundButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSoundClick(index);
                  }}
                  aria-label={playingIndex === index ? 'Mute' : 'Unmute'}
                >
                  {playingIndex === index ? <UnmutedIcon /> : <MutedIcon />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TikTokHeroNew;
